/**
 * Helpers for "translatable" branding fields — currently `branding.tagline`
 * and `branding.hero_description`.
 *
 * Storage convention: the value persisted at /tenant:{code} (or /global) is
 * a JSON object keyed by locale code, e.g.
 *
 *   {"en-US":"Crafting Tomorrow's...","zh-TW":"打造明日的..."}
 *
 * Legacy plain strings (no JSON wrapping) keep working — they apply to every
 * locale. The backend treats the value as an opaque string; parsing happens
 * here on the frontend.
 *
 * The set of translatable keys is defined on the backend in
 * `BrandingConstants.TranslatableKeys` and surfaced on each
 * `TenantBrandingField` via `isTranslatable: boolean`.
 */

import type { LanguageCode } from '@/app/store/slices/languageSlice';

/**
 * Decode a stored translatable value into its locale → text map. Accepts
 * three shapes gracefully:
 *   • JSON object `{"en-US":"…","zh-TW":"…"}` → returns it as-is.
 *   • Plain string                          → returns `{"en-US": value}` so
 *                                              callers get a uniform shape.
 *   • Empty / null                          → returns `{}`.
 *
 * Malformed JSON (e.g. unclosed quotes) is treated as a plain string —
 * never throws.
 */
export function parseTranslatable(raw: string | null | undefined): Record<string, string> {
  if (!raw) return {};
  const trimmed = raw.trim();
  // Quick check before paying for JSON.parse — a translation object always
  // starts with '{'.
  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        const out: Record<string, string> = {};
        for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
          if (typeof v === 'string') out[k] = v;
        }
        return out;
      }
    } catch {
      /* fall through and treat as plain string */
    }
  }
  return { 'en-US': raw };
}

/**
 * Encode a locale → text map back to the storage shape. Empty entries are
 * dropped (we never persist `""` for a locale — `undefined` means "no
 * translation, fall back to another locale"). When EVERY entry is empty,
 * returns `""` so the field becomes Inherited again.
 */
export function serializeTranslatable(map: Record<string, string>): string {
  const cleaned: Record<string, string> = {};
  for (const [k, v] of Object.entries(map)) {
    if (typeof v === 'string' && v.length > 0) cleaned[k] = v;
  }
  if (Object.keys(cleaned).length === 0) return '';
  return JSON.stringify(cleaned);
}

/**
 * Resolve a translatable value to the right text for the current locale.
 * Fallback chain:
 *   currentLocale → en-US → first available → inheritedFallback
 *
 * `inheritedFallback` is whatever the consumer wants to show when nothing
 * is filled in (typically the localized default from the i18n locale file,
 * e.g. `t('hero.description')`). The fallback also kicks in if the value
 * came back as an empty map.
 */
export function resolveTranslatable(
  raw: string | null | undefined,
  currentLocale: LanguageCode | string,
  inheritedFallback: string,
): string {
  const map = parseTranslatable(raw);
  const keys = Object.keys(map);
  if (keys.length === 0) return inheritedFallback;
  if (map[currentLocale]) return map[currentLocale];
  if (map['en-US']) return map['en-US'];
  // Any non-empty entry beats the fallback.
  const first = keys.find((k) => map[k]);
  return first ? map[first] : inheritedFallback;
}
